import { FC, useState, useRef, ReactNode } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { FileUpload, FileUploadSelectEvent } from 'primereact/fileupload';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import { ExcelImportResult, ExcelImportRowError } from '../../models/fees';
import * as XLSX from 'xlsx';

export type ExcelRow = (string | number | boolean | null | undefined)[];
export type ExcelData = ExcelRow[];

export interface ImportDialogProps<TPreview> {
  visible: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<ExcelImportResult>;
  title: string;
  entityName: string;
  parsePreview: (dataRows: ExcelData) => TPreview;
  renderPreview: (preview: TPreview, fileName: string) => ReactNode;
}

type ImportStep = 'select' | 'importing' | 'results';

export function ImportDialog<TPreview>({
  visible,
  onClose,
  onImport,
  title,
  entityName,
  parsePreview,
  renderPreview,
}: ImportDialogProps<TPreview>) {
  const [step, setStep] = useState<ImportStep>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ExcelImportResult | null>(null);
  const [previewData, setPreviewData] = useState<TPreview | null>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const handleFileSelect = (event: FileUploadSelectEvent) => {
    if (event.files && event.files.length > 0) {
      const file = event.files[0];
      setSelectedFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as ExcelData;

          const dataRows = jsonData.slice(1).filter((row): row is ExcelRow => row !== null && row !== undefined && row.length > 0);
          const preview = parsePreview(dataRows);
          setPreviewData(preview);
        } catch (error) {
          console.error('Failed to parse Excel file:', error);
          setPreviewData(null);
        }
      };
      reader.readAsBinaryString(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setStep('importing');
    try {
      const result = await onImport(selectedFile);
      setImportResult(result);
      setStep('results');
    } catch (error) {
      console.error('Import failed:', error);
      setImportResult({
        totalRows: 0,
        successfulRows: 0,
        success: false,
        failedRows: 1,
        errors: [{
          rowIdentifier: '',
          rowNumber: 0,
          errorMessage: 'Import failed: ' + (error instanceof Error ? error.message : 'Unknown error'),
        }],
        warnings: null,
      });
      setStep('results');
    }
  };

  const handleClose = () => {
    setStep('select');
    setSelectedFile(null);
    setImportResult(null);
    setPreviewData(null);
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
    onClose();
  };

  const renderSelectStep = () => (
    <div className="flex flex-column gap-4">
      <FileUpload
        ref={fileUploadRef}
        name="file"
        accept=".xlsx,.xls"
        maxFileSize={10000000}
        customUpload
        uploadHandler={handleImport}
        onSelect={handleFileSelect}
        onClear={() => {
          setSelectedFile(null);
          setPreviewData(null);
        }}
        chooseLabel="Select File"
        uploadLabel="Import"
        cancelLabel="Clear"
        disabled={step !== 'select'}
        emptyTemplate={
          <div className="flex align-items-center flex-column" style={{ padding: '3rem' }}>
            <i className="pi pi-cloud-upload" style={{ fontSize: '3em', color: 'var(--primary-color)' }}></i>
            <span style={{ fontSize: '1.2em', color: 'var(--text-color-secondary)' }} className="my-3">
              Drag and drop Excel file here or click to browse
            </span>
          </div>
        }
      />

      {selectedFile && previewData && (
        <>{renderPreview(previewData, selectedFile.name)}</>
      )}
    </div>
  );

  const renderImportingStep = () => (
    <div className="flex flex-column align-items-center justify-content-center gap-3" style={{ minHeight: '300px' }}>
      <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
      <span className="text-xl">Importing {entityName}...</span>
      <span className="text-color-secondary">Please wait while we process your file.</span>
    </div>
  );

  const renderResultsStep = () => {
    if (!importResult) return null;

    const hasErrors = importResult.errors && importResult.errors.length > 0;
    const allSuccess = importResult.successfulRows === importResult.totalRows;

    return (
      <div className="flex flex-column gap-4">
        <div className="surface-100 p-4 border-round">
          <h3 className="mt-0 mb-3">Import Summary</h3>
          <div className="flex flex-column gap-2">
            <div className="flex justify-content-between align-items-center">
              <span className="font-semibold">Total Rows:</span>
              <span className="text-xl">{importResult.totalRows}</span>
            </div>
            <div className="flex justify-content-between align-items-center">
              <span className="font-semibold text-green-600">Successful:</span>
              <span className="text-xl text-green-600">{importResult.successfulRows}</span>
            </div>
            {hasErrors && (
              <div className="flex justify-content-between align-items-center">
                <span className="font-semibold text-red-600">Failed:</span>
                <span className="text-xl text-red-600">{importResult.errors?.length || 0}</span>
              </div>
            )}
          </div>
        </div>

        {allSuccess && (
          <Message severity="success" text={`All ${entityName} were imported successfully!`} />
        )}

        {hasErrors && (
          <div className="flex flex-column gap-3">
            <Message
              severity="error"
              text={`${importResult.errors?.length || 0} row(s) failed to import. See details below.`}
            />

            <div className="surface-100 p-3 border-round">
              <h4 className="mt-0 mb-3">Import Errors</h4>
              <DataTable
                value={importResult.errors || []}
                scrollable
                scrollHeight="300px"
                emptyMessage="No errors"
                size="small"
              >
                <Column
                  field="rowNumber"
                  header="Row"
                  style={{ width: '80px' }}
                  body={(rowData: ExcelImportRowError) => rowData.rowNumber || 'N/A'}
                />
                <Column
                  field="rowIdentifier"
                  header="Identifier"
                  style={{ width: '200px' }}
                  body={(rowData: ExcelImportRowError) => rowData.rowIdentifier || 'N/A'}
                />
                <Column
                  field="errorMessage"
                  header="Error"
                  body={(rowData: ExcelImportRowError) => (
                    <span className="text-red-600">{rowData.errorMessage || 'Unknown error'}</span>
                  )}
                />
              </DataTable>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getFooter = () => {
    switch (step) {
      case 'select':
        return (
          <div className="flex justify-content-end gap-2">
            <Button label="Close" className="p-button-secondary" onClick={handleClose} />
          </div>
        );
      case 'importing':
        return null;
      case 'results':
        return (
          <div className="flex justify-content-end gap-2">
            <Button label="Close" className="p-button-primary" onClick={handleClose} icon="pi pi-check" />
          </div>
        );
      default:
        return null;
    }
  };

  const getHeader = () => {
    switch (step) {
      case 'select':
        return title;
      case 'importing':
        return 'Importing...';
      case 'results':
        return 'Import Results';
      default:
        return title;
    }
  };

  return (
    <Dialog
      header={getHeader()}
      visible={visible}
      onHide={handleClose}
      footer={getFooter()}
      style={{ width: '800px' }}
      modal
      closable={step !== 'importing'}
      closeOnEscape={step !== 'importing'}
    >
      {step === 'select' && renderSelectStep()}
      {step === 'importing' && renderImportingStep()}
      {step === 'results' && renderResultsStep()}
    </Dialog>
  );
}
